import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';
import {
    CloudSyncOutlined,
    FontSizeOutlined,
    PictureOutlined,
    VideoCameraOutlined,
    FileTextOutlined,
    ApiOutlined,
    NodeIndexOutlined,
    SoundOutlined,
    ScissorOutlined,
    GatewayOutlined,
    AppstoreOutlined,
    RobotOutlined
} from '@ant-design/icons';

const iconMap = {
    'cloud-sync': <CloudSyncOutlined />,
    'font-size': <FontSizeOutlined />,
    'picture': <PictureOutlined />,
    'video-camera': <VideoCameraOutlined />,
    'file-text': <FileTextOutlined />,
    'api': <ApiOutlined />,
    'sound': <SoundOutlined />,
    'scissor': <ScissorOutlined />,
    'gateway': <GatewayOutlined />,
    'appstore': <AppstoreOutlined />,
    'robot': <RobotOutlined />,
    'default': <NodeIndexOutlined />
};

const NodeLabel = ({ title, iconType, color = '#1890ff', editable = true, onTitleChange, children }) => {
    const icon = iconMap[iconType] || iconMap['default'];
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(title);
    const inputRef = useRef(null);

    // Sync title prop with local state when not editing
    useEffect(() => {
        if (!isEditing) {
            setEditValue(title);
        }
    }, [title, isEditing]);

    const handleDoubleClick = (e) => {
        // Prevent event from bubbling up to node drag handlers
        e.stopPropagation();
        if (editable && onTitleChange) {
            setIsEditing(true);
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 50);
        }
    };

    const handleFinishEdit = () => {
        setIsEditing(false);
        if (editValue.trim() !== '' && editValue !== title) {
            onTitleChange(editValue);
        } else {
            setEditValue(title);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleFinishEdit();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditValue(title);
        }
    };

    return (
        <div
            style={{
                position: 'absolute',
                top: -40,
                left: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'transparent',
                padding: 0,
                zIndex: 10,
                whiteSpace: 'nowrap'
            }}
        >
            <span style={{
                color: color,
                fontSize: 18,
                display: 'flex',
                alignItems: 'center',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
            }}>{icon}</span>

            {isEditing ? (
                <Input
                    ref={inputRef}
                    className="nodrag"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleFinishEdit}
                    onKeyDown={handleKeyDown}
                    style={{
                        fontSize: 14,
                        fontWeight: 600,
                        height: 24,
                        background: 'rgba(0,0,0,0.4)',
                        border: 'none',
                        borderRadius: 4,
                        color: '#fff',
                        width: 'auto',
                        minWidth: 100,
                        padding: '2px 8px'
                    }}
                />
            ) : (
                <span
                    onDoubleClick={handleDoubleClick}
                    style={{
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: editable ? 'text' : 'default',
                        userSelect: 'none',
                        textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                        letterSpacing: '0.02em'
                    }}
                >
                    {title}
                </span>
            )}
            {children}
        </div>
    );
};

export default NodeLabel;
